import { LANGUAGES } from "../data/constants";
import { getLangFont } from "../utils/helpers";
import LangDropdown from "./LangDropdown";

// ============================================================
// TrendBadge
// ============================================================
/**
 * Displays a coloured up/down/flat trend indicator.
 *
 * Props:
 *   trend  {"up"|"down"|"same"}
 *   change {number}  – absolute ₹ change value
 */
export function TrendBadge({ trend, change }) {
  if (trend === "up")
    return <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 13 }}>↑ +₹{change}</span>;
  if (trend === "down")
    return <span style={{ color: "#dc2626", fontWeight: 600, fontSize: 13 }}>↓ -₹{change}</span>;
  return <span style={{ color: "#9ca3af", fontSize: 13 }}>— —</span>;
}

// ============================================================
// Navbar
// ============================================================
/**
 * Sticky top navigation bar.
 *
 * Props:
 *   lang    {string}
 *   setLang {function}
 *   page    {string}
 *   setPage {function}
 *   t       {object}  – translations object
 */
export function Navbar({ lang, setLang, page, setPage, t }) {
  const bf    = getLangFont(lang);
  const isRTL = lang === "ur";

  return (
    <nav
      style={{
        position:       "sticky",
        top:            0,
        zIndex:         200,
        background:     "rgba(255,252,245,0.95)",
        backdropFilter: "blur(16px)",
        borderBottom:   "1px solid #e8e0d0",
        boxShadow:      "0 1px 10px rgba(0,0,0,0.06)",
        direction:      isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          maxWidth:      1100,
          margin:        "0 auto",
          padding:       "0 16px",
          display:       "flex",
          alignItems:    "center",
          justifyContent:"space-between",
          height:        56,
        }}
      >
        {/* Logo */}
        <div
          onClick={() => setPage("home")}
          style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}
        >
          <div
            style={{
              width:        34,
              height:       34,
              background:   "linear-gradient(135deg,#1a4d2e,#40916c)",
              borderRadius: 10,
              display:      "flex",
              alignItems:   "center",
              justifyContent:"center",
              fontSize:     16,
            }}
          >
            🌾
          </div>
          <div>
            <div style={{ fontFamily: getLangFont(lang), fontSize: 16, color: "#1a4d2e", lineHeight: 1 }}>
              {t.title}
            </div>
            <div style={{ fontSize: 9, color: "#9ca3af", letterSpacing: 0.5, fontFamily: "'DM Sans', sans-serif" }}>
              {t.subtitle}
            </div>
          </div>
        </div>

        {/* Nav links + language picker */}
        <div style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          {[
            ["home",     t.navHome],
            ["rates",    t.navRates],
            //["register", t.navReg],
            //["admin",    t.navAdmin],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPage(key)}
              style={{
                padding:      "6px 11px",
                borderRadius: 20,
                border:       "none",
                cursor:       "pointer",
                fontFamily:   bf,
                fontSize:     13,
                fontWeight:   500,
                background:   page === key ? "#1a4d2e" : "transparent",
                color:        page === key ? "#fff" : "#555",
                transition:   "all 0.18s",
              }}
            >
              {label}
            </button>
          ))}
          <div style={{ marginLeft: 6 }}>
            <LangDropdown lang={lang} setLang={setLang} />
          </div>
        </div>
      </div>
    </nav>
  );
}

// ============================================================
// Footer
// ============================================================
/**
 * Site-wide footer with navigation links, language switcher, and data-source credit.
 *
 * Props:
 *   lang    {string}
 *   setLang {function}
 *   t       {object}
 */
export function Footer({ lang, setLang, t }) {
  const bf    = getLangFont(lang);
  const isRTL = lang === "ur";

  return (
    <footer
      style={{
        background: "#1a4d2e",
        color:      "#fff",
        padding:    "36px 24px",
        direction:  isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          maxWidth:             1100,
          margin:               "0 auto",
          display:              "grid",
          gridTemplateColumns:  "repeat(auto-fit,minmax(190px,1fr))",
          gap:                  24,
          fontFamily:           bf,
        }}
      >
        {/* Brand */}
        <div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, marginBottom: 8 }}>
            🌾 {t.title}
          </div>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>
            {lang === "en"
              ? "Empowering Indian farmers with real-time market intelligence."
              : "किसानों के लिए, किसानों द्वारा।"}
          </p>
        </div>

        {/* Navigation links */}
        <div>
          <div
            style={{
              fontWeight:    700,
              fontSize:      10,
              letterSpacing: 1,
              textTransform: "uppercase",
              color:         "rgba(255,255,255,0.4)",
              marginBottom:  12,
            }}
          >
            Navigation
          </div>
          {[t.navHome, t.navRates, t.navReg].map((l) => (
            <div
              key={l}
              style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 7, cursor: "pointer" }}
            >
              {l}
            </div>
          ))}
        </div>

        {/* Language switcher */}
        <div>
          <div
            style={{
              fontWeight:    700,
              fontSize:      10,
              letterSpacing: 1,
              textTransform: "uppercase",
              color:         "rgba(255,255,255,0.4)",
              marginBottom:  12,
            }}
          >
            🌐 Languages
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 8px" }}>
            {LANGUAGES.map((l) => (
              <span
                key={l.code}
                onClick={() => setLang(l.code)}
                style={{
                  cursor:     "pointer",
                  fontSize:   12,
                  color:      lang === l.code ? "#52c27e" : "rgba(255,255,255,0.45)",
                  fontWeight: lang === l.code ? 700 : 400,
                  fontFamily: l.font,
                }}
              >
                {l.nativeLabel}
              </span>
            ))}
          </div>
        </div>

        {/* Data source */}
        <div>
          <div
            style={{
              fontWeight:    700,
              fontSize:      10,
              letterSpacing: 1,
              textTransform: "uppercase",
              color:         "rgba(255,255,255,0.4)",
              marginBottom:  12,
            }}
          >
            Data Source
          </div>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.8 }}>
            AGMARKNET · data.gov.in
            <br />
            <span style={{ fontSize: 11, opacity: 0.5 }}>Government of India · Open API</span>
          </p>
        </div>
      </div>

      <div
        style={{
          textAlign:   "center",
          marginTop:   32,
          paddingTop:  18,
          borderTop:   "1px solid rgba(255,255,255,0.1)",
          fontSize:    12,
          color:       "rgba(255,255,255,0.3)",
          fontFamily:  "'DM Sans', sans-serif",
        }}
      >
        © 2026 MandiMitra · किसानों के लिए, किसानों द्वारा · Built with ❤️ for Bharat
      </div>
    </footer>
  );
}

// ============================================================
// WhatsApp FAB (Floating Action Button)
// ============================================================
/**
 * Fixed bottom-right WhatsApp CTA shown on non-register pages.
 *
 * Props:
 *   lang    {string}
 *   isRTL   {boolean}
 *   t       {object}
 *   onClick {function}
 */
export function WhatsAppFAB({ lang, isRTL, t, onClick }) {
  const bf = getLangFont(lang);
  return (
    <button
      onClick={onClick}
      style={{
        position:     "fixed",
        bottom:       22,
        right:        isRTL ? "auto" : 22,
        left:         isRTL ? 22 : "auto",
        zIndex:       500,
        background:   "#25D366",
        color:        "#fff",
        border:       "none",
        borderRadius: 50,
        padding:      "12px 18px",
        fontSize:     14,
        fontWeight:   700,
        cursor:       "pointer",
        fontFamily:   bf,
        boxShadow:    "0 6px 24px rgba(37,211,102,0.4)",
        display:      "flex",
        alignItems:   "center",
        gap:          6,
        animation:    "fadeIn 0.5s ease 0.8s both",
      }}
    >
      📲 {t.whatsapp}
    </button>
  );
}
